import type { PermissionCatalogItem } from "@/types/permission-catalog";

export type FrontdeskBlock =
  | "frontdesk_hotel_settings"
  | "frontdesk_dashboard"
  | "frontdesk_inbox"
  | "frontdesk_guests"
  | "frontdesk_management";

export type FrontdeskAccessLevel = "none" | "read" | "administrate";

export interface FrontdeskBlockDefinition {
  resource: FrontdeskBlock;
  label: string;
  description: string;
  landingPath: string;
}

export const FRONTDESK_BLOCKS: readonly FrontdeskBlockDefinition[] = [
  {
    resource: "frontdesk_hotel_settings",
    label: "Hotel Settings",
    description: "Hotel settings and all settings tabs",
    landingPath: "/general-info",
  },
  {
    resource: "frontdesk_dashboard",
    label: "Dashboard",
    description: "Frontdesk dashboard",
    landingPath: "/dashboard",
  },
  {
    resource: "frontdesk_inbox",
    label: "Inbox",
    description: "Chats, requests, and feedbacks",
    landingPath: "/chats",
  },
  {
    resource: "frontdesk_guests",
    label: "Guests",
    description:
      "Arrivals, in-house, departures, reservations, rooms, and guests",
    landingPath: "/arrivals",
  },
  {
    resource: "frontdesk_management",
    label: "Management",
    description: "Services, staff, internal chat, and analytics",
    landingPath: "/services",
  },
] as const;

export const FRONTDESK_PERMISSION_CATALOG: PermissionCatalogItem[] =
  FRONTDESK_BLOCKS.flatMap(({ resource, label, description }) => [
    {
      key: `${resource}:read`,
      resource,
      action: "read",
      label: `View ${label}`,
      description,
      category: label,
    },
    {
      key: `${resource}:administrate`,
      resource,
      action: "administrate",
      label: `Administrate ${label}`,
      description,
      category: label,
    },
  ]);

export function getFrontdeskAccessLevel(
  permissionKeys: readonly string[],
  resource: FrontdeskBlock,
): FrontdeskAccessLevel {
  if (permissionKeys.includes(`${resource}:administrate`)) {
    return "administrate";
  }
  if (permissionKeys.includes(`${resource}:read`)) {
    return "read";
  }
  return "none";
}

export function setFrontdeskAccessLevel(
  permissionKeys: readonly string[],
  resource: FrontdeskBlock,
  level: FrontdeskAccessLevel,
): string[] {
  const next = permissionKeys.filter(
    (key) => key !== `${resource}:read` && key !== `${resource}:administrate`,
  );

  if (level === "read") {
    next.push(`${resource}:read`);
  } else if (level === "administrate") {
    next.push(`${resource}:administrate`);
  }

  return next;
}

const HOTEL_SETTINGS_PATH_PREFIXES = [
  "/general-info",
  "/amenities",
  "/custom-pages",
  "/faqs",
  "/canned-responses",
  "/checkin-settings",
  "/checkout-settings",
  "/mobile-settings",
  "/pms-integration",
  "/notifications-settings",
  "/messaging-settings",
  "/communication-",
] as const;

export function getFrontdeskBlockForPath(
  pathname: string,
): FrontdeskBlock | null {
  if (pathname === "/dashboard") return "frontdesk_dashboard";
  if (
    pathname.startsWith("/chats") ||
    pathname.startsWith("/requests") ||
    pathname.startsWith("/feedback")
  ) {
    return "frontdesk_inbox";
  }
  if (
    pathname.startsWith("/arrivals") ||
    pathname.startsWith("/in-house") ||
    pathname.startsWith("/departures") ||
    pathname.startsWith("/reservations")
  ) {
    return "frontdesk_guests";
  }
  if (
    pathname.startsWith("/services") ||
    pathname.startsWith("/staff") ||
    pathname.startsWith("/internal-chats") ||
    pathname.startsWith("/analytics")
  ) {
    return "frontdesk_management";
  }
  if (
    HOTEL_SETTINGS_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  ) {
    return "frontdesk_hotel_settings";
  }
  return null;
}

const FRONTDESK_BLOCK_BY_DOMAIN_RESOURCE: Readonly<
  Record<string, FrontdeskBlock>
> = {
  settings: "frontdesk_hotel_settings",
  bookings: "frontdesk_guests",
  rooms: "frontdesk_guests",
  guests: "frontdesk_guests",
  requests: "frontdesk_inbox",
  feedbacks: "frontdesk_inbox",
  guest_chat: "frontdesk_inbox",
  services: "frontdesk_management",
  staff: "frontdesk_management",
  analytics: "frontdesk_management",
  internal_chat: "frontdesk_management",
};

export function getFrontdeskBlockForResource(
  resource: string,
): FrontdeskBlock | null {
  return FRONTDESK_BLOCK_BY_DOMAIN_RESOURCE[resource] ?? null;
}
